import { moneyFormatNoDecimal } from "@sveabank/cc-common";
import type {
	ClcBeneficialOwner,
	ClcPerson,
	ClcProductQuestionsStep,
	ClcRepresentative,
	ClcShareholder,
} from "../../types/clc-application";
import type { SelectedClcPackage } from "../../types/package";

export const normalizeNumericValue = (value: string): number =>
	Number.parseInt(value.replace(/[^0-9]/g, ""), 10);

export const parseNumericValue = (value: string): number | undefined => {
	const parsed = normalizeNumericValue(value);
	return Number.isNaN(parsed) ? undefined : parsed;
};

export const formatNumericValue = (value: number | null | undefined): string =>
	value === null || value === undefined ? "" : moneyFormatNoDecimal(value);

export const getTotalDistributedShares = (shareholders: Array<ClcShareholder> = []) =>
	shareholders.reduce((total, curr) => total + curr.numberOfShares, 0) || 0;

export const getIsPersonShareholder = (
	nationalId: string,
	shareholders: Array<ClcShareholder> = [],
) => !!shareholders.map((s) => s.nationalId).includes(nationalId);

export const areClcProductAmlQuestionsComplete = (
	productId: string,
	productQuestionStep: ClcProductQuestionsStep,
) => productQuestionStep.some((p) => p.productId === productId && p.isComplete);

export const getCardholderPersonDetails = (
	availableCardHolders: Array<{ nationalId: string; displayNames: string[] }>,
	shareholders: Array<ClcShareholder>,
	companyRepresentatives: Array<ClcRepresentative>,
	beneficialOwners: Array<ClcBeneficialOwner>,
): Array<ClcPerson> => {
	const data = [...shareholders, ...companyRepresentatives, ...beneficialOwners];

	return availableCardHolders
		.map((cardholder) => data.find((person) => person.nationalId === cardholder.nationalId))
		.filter((person): person is ClcPerson => person !== undefined)
		.map(
			(p) =>
				({
					firstName: p.firstName,
					lastName: p.lastName,
					nationalId: p.nationalId,
					postCode: p.postCode,
					postalAddress: p.postalAddress,
					town: p.town,
					email: p.email || null,
					phone: p.phone || null,
				}) satisfies ClcPerson,
		);
};

export const getTotalAddonCost = (selectedPackage: SelectedClcPackage) =>
	selectedPackage.products
		.filter((p) => p.isAddon)
		.reduce((sum, curr) => sum + Number(curr.monthlyFee.amount), 0);

export const getDebitCardAddonCost = (selectedPackage: SelectedClcPackage) =>
	selectedPackage.availableAddons.find((a) => a.productDefinitionId === "DEBITCARD")?.monthlyFee
		.amount || "0";
